import React, { lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { ThemeProvider } from '@/components/theme-provider'
import { AppShell } from './shell/AppShell'
import { AuthGate } from './shell/AuthGate'
import { DashboardPage } from './pages/DashboardPage'
import { AttentionPage } from './pages/AttentionPage'
import { WarehousePage } from './pages/WarehousePage'
import { ParcelsPage } from './pages/ParcelsPage'
import { ConsignmentsPage } from './pages/ConsignmentsPage'
import { ConsignmentDetailPage } from './pages/ConsignmentDetailPage'
import { InquiriesPage } from './pages/InquiriesPage'
import { AssistantPage } from './pages/AssistantPage'
import { InquiryDetailPage } from './pages/InquiryDetailPage'
import { NewInquiryPage } from './pages/NewInquiryPage'
import { CustomerListPage } from './pages/CustomerListPage'
import { NewCustomerPage } from './pages/NewCustomerPage'
import {
  CustomerDetailPage,
  CustomerActivityPage,
  CustomerConsignmentsPage,
  CustomerInvoicesPage,
  CustomerOverviewPage,
} from './pages/CustomerDetailPage'
import { SettingsPage } from './pages/SettingsPage'
import { WhatsAppPage } from './pages/WhatsAppPage'
import { UsersPage } from './pages/UsersPage'
import { AcceptInvitationPage } from './pages/AcceptInvitationPage'
import { DocsPage } from './pages/DocsPage'
import { LoginPage } from './pages/LoginPage'
import { InvitePage } from './pages/InvitePage'
import { ConfirmSignInEmailPage } from './pages/ConfirmSignInEmailPage'
import { NotFoundPage } from './pages/NotFoundPage'
import { PublicTrackingPage } from './pages/PublicTrackingPage'
import { TerminalRoute } from './pages/TerminalRoute'
import { AssistantSessionProvider } from './components/assistant/AssistantSession'
import { initHyperDXBrowserTelemetry, recordHyperDXPageView } from './lib/hyperdx-browser'
import { installDiagnostics } from './lib/diagnostics'
import { API_BASE_URL } from '@/lib/env'
import { consumePublicTrackingFragment } from './lib/public-tracking'
import { setUpInstalledApp } from './lib/pwa'
import NewConsignmentPage from '@/app/(dashboard)/consignments/new/page'

import '@/app/globals.css'

// Full-screen pages for the office monitor, loaded only when opened.
const WallPage = lazy(() =>
  import('./pages/WallPage').then((module) => ({ default: module.WallPage })),
)

setUpInstalledApp()

const publicTrackingRoute = window.location.pathname.match(/^\/s\/([^/]+)\/?$/)
if (publicTrackingRoute) {
  consumePublicTrackingFragment(decodeURIComponent(publicTrackingRoute[1]))
}

const router = createBrowserRouter([
  {
    path: '/s/:publicId',
    element: <PublicTrackingPage />,
  },
  {
    // The office wall: full screen, outside the app's menu and header.
    path: '/wall',
    element: (
      <AuthGate>
        {() => (
          <Suspense fallback={null}>
            <WallPage />
          </Suspense>
        )}
      </AuthGate>
    ),
    errorElement: <NotFoundPage />,
  },
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <NotFoundPage />,
  },
  { path: '/terminal', element: <TerminalRoute />, errorElement: <NotFoundPage /> },
  {
    // Public: an invited person opens this before they have an account.
    path: '/invite',
    element: <InvitePage />,
    errorElement: <NotFoundPage />,
  },
  {
    // Public: the link in a "confirm your new sign-in email" message, opened
    // from that inbox whether or not this browser is signed in.
    path: '/account/confirm-email',
    element: <ConfirmSignInEmailPage />,
    errorElement: <NotFoundPage />,
  },
  {
    path: '/',
    element: <AppShell />,
    errorElement: <NotFoundPage />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'attention', element: <AttentionPage /> },
      { path: 'warehouse', element: <WarehousePage /> },
      { path: 'parcels', element: <ParcelsPage /> },
      { path: 'assistant', element: <AssistantPage /> },
      { path: 'inquiries', element: <InquiriesPage /> },
      { path: 'inquiries/new', element: <NewInquiryPage /> },
      { path: 'inquiries/:id', element: <InquiryDetailPage /> },
      { path: 'consignments', element: <ConsignmentsPage /> },
      { path: 'consignments/new', element: <NewConsignmentPage /> },
      { path: 'consignments/:id', element: <ConsignmentDetailPage /> },
      { path: 'consignments/:id/:tab', element: <ConsignmentDetailPage /> },
      { path: 'customers', element: <CustomerListPage /> },
      { path: 'customers/new', element: <NewCustomerPage /> },
      {
        path: 'customers/:id',
        element: <CustomerDetailPage />,
        children: [
          { index: true, element: <CustomerOverviewPage /> },
          { path: 'consignments', element: <CustomerConsignmentsPage /> },
          { path: 'invoices', element: <CustomerInvoicesPage /> },
          { path: 'activity', element: <CustomerActivityPage /> },
        ],
      },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'settings/whatsapp', element: <WhatsAppPage /> },
      { path: 'settings/users', element: <UsersPage /> },
      { path: 'settings/users/accept', element: <AcceptInvitationPage /> },
      { path: 'docs', element: <DocsPage /> },
      { path: 'docs/*', element: <DocsPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])

initHyperDXBrowserTelemetry()
recordHyperDXPageView(`${window.location.pathname}${window.location.search}`)
router.subscribe((state) => {
  recordHyperDXPageView(`${state.location.pathname}${state.location.search}`)
})

// Early, so a bug report can include what went wrong before it was opened.
installDiagnostics(API_BASE_URL)

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      {/* Above the router, so Beacon's conversation and any call survive navigation. */}
      <AssistantSessionProvider>
        <RouterProvider router={router} />
      </AssistantSessionProvider>
    </ThemeProvider>
  </React.StrictMode>,
)
